import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useWindows } from "./useWindows";
import Window from "./Window";
import Taskbar from "./Taskbar";
import DesktopIcon from "./DesktopIcon";
import { playBeep } from "./sound";
import { projects } from "../data/portfolio";
import AboutWindow from "../windows/AboutWindow";
import ProjectsWindow from "../windows/ProjectsWindow";
import ProjectDetailWindow from "../windows/ProjectDetailWindow";
import SkillsWindow from "../windows/SkillsWindow";
import EducationWindow from "../windows/EducationWindow";
import CertificationsWindow from "../windows/CertificationsWindow";
import ContactWindow from "../windows/ContactWindow";
import RecycleBinWindow from "../windows/RecycleBinWindow";
import ShutdownOverlay from "../windows/ShutdownOverlay";
import PongWindow from "../windows/PongWindow";
import GameHubWindow from "../windows/GameHubWindow";

const ICON_SECTIONS = [
  { id: "about", icon: "🧑‍💻", label: "About Me" },
  { id: "projects", icon: "📁", label: "My Projects" },
  { id: "skills", icon: "⚙️", label: "Skills" },
  { id: "education", icon: "🎓", label: "Education" },
  { id: "certifications", icon: "🏆", label: "Certifications" },
  { id: "contact", icon: "✉️", label: "Contact" },
  { id: "gamehub", icon: "🕹️", label: "Games" },
  { id: "recyclebin", icon: "🗑️", label: "Recycle Bin" },
];

const REGISTRY = {
  about: { title: "ABOUT.TXT - Notepad", icon: "🧑‍💻", width: 720, height: 560, startMaximized: false },
  projects: { title: "My Projects", icon: "📁", width: 680, height: 600 },
  skills: { title: "System Properties", icon: "⚙️", width: 620, height: 460 },
  education: { title: "My Documents - Education", icon: "🎓", width: 640, height: 480 },
  certifications: { title: "Control Panel - Certifications", icon: "🏆", width: 560, height: 480 },
  contact: { title: "New Message", icon: "✉️", width: 600, height: 480 },
  gamehub: { title: "Games", icon: "🕹️", width: 560, height: 420 },
  pong: { title: "PONG.EXE", icon: "🏓", width: 860, height: 560 },
  recyclebin: { title: "Recycle Bin", icon: "🗑️", width: 380, height: 320 },
};

projects.forEach((project) => {
  REGISTRY[`project/${project.slug}`] = {
    title: `${project.title}.txt`,
    icon: "📄",
    width: 760,
    height: 620,
  };
});

const Desktop = () => {
  const {
    windows,
    isMobile,
    openWindow,
    closeWindow,
    minimizeWindow,
    toggleMaximize,
    moveWindow,
    focusWindow,
    restoreOrToggle,
  } = useWindows(REGISTRY);

  const [selectedIcon, setSelectedIcon] = useState(null);
  const [soundOn, setSoundOn] = useState(false);
  const [shutdownOpen, setShutdownOpen] = useState(false);

  useEffect(() => {
    if (!window.location.hash) {
      openWindow("about");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const navigate = useCallback(
    (id) => {
      playBeep(680, 0.04, soundOn);
      openWindow(id);
      if (window.location.hash.replace(/^#/, "") !== id) {
        window.location.hash = id;
      }
    },
    [openWindow, soundOn]
  );

  const handleClose = useCallback(
    (id) => {
      playBeep(320, 0.05, soundOn);
      closeWindow(id);
    },
    [closeWindow, soundOn]
  );

  const topZ = windows.length ? Math.max(...windows.map((w) => w.z)) : -1;

  const getContent = (id) => {
    if (id.startsWith("project/")) {
      const project = projects.find((p) => `project/${p.slug}` === id);
      if (!project) return null;
      return <ProjectDetailWindow project={project} navigate={navigate} />;
    }
    switch (id) {
      case "about":
        return <AboutWindow navigate={navigate} />;
      case "projects":
        return <ProjectsWindow navigate={navigate} />;
      case "skills":
        return <SkillsWindow />;
      case "education":
        return <EducationWindow />;
      case "certifications":
        return <CertificationsWindow />;
      case "contact":
        return <ContactWindow />;
      case "gamehub":
        return <GameHubWindow navigate={navigate} />;
      case "pong":
        return <PongWindow />;
      case "recyclebin":
        return <RecycleBinWindow navigate={navigate} />;
      default:
        return null;
    }
  };

  const menuItems = useMemo(
    () => ICON_SECTIONS.map((section) => ({ id: section.id, icon: section.icon, label: section.label })),
    []
  );

  return (
    <div
      className="win-desktop-bg fixed inset-0 overflow-hidden"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) setSelectedIcon(null);
      }}
    >
      <div className="flex flex-col flex-wrap content-start gap-1 p-3" style={{ height: "calc(100vh - 44px)" }}>
        {ICON_SECTIONS.map((section) => (
          <DesktopIcon
            key={section.id}
            icon={section.icon}
            label={section.label}
            isMobile={isMobile}
            selected={selectedIcon === section.id}
            onSelect={() => setSelectedIcon(section.id)}
            onOpen={() => {
              setSelectedIcon(section.id);
              navigate(section.id);
            }}
          />
        ))}
      </div>

      {windows.map((win) => (
        <Window
          key={win.id}
          win={win}
          isActive={win.z === topZ}
          isMobile={isMobile}
          onClose={handleClose}
          onMinimize={minimizeWindow}
          onToggleMaximize={toggleMaximize}
          onFocus={focusWindow}
          onMove={moveWindow}
        >
          {getContent(win.id)}
        </Window>
      ))}

      <Taskbar
        menuItems={menuItems}
        windows={windows}
        onOpen={navigate}
        onRestoreOrToggle={restoreOrToggle}
        soundOn={soundOn}
        onToggleSound={() => setSoundOn((v) => !v)}
        onShutDown={() => setShutdownOpen(true)}
      />

      {shutdownOpen && <ShutdownOverlay onClose={() => setShutdownOpen(false)} />}
    </div>
  );
};

export default Desktop;
